import { MakeLogicType, actions, connect, kea, listeners, path, reducers } from 'kea'
import { forms } from 'kea-forms'
import type { DeepPartial, DeepPartialMap, FieldName, ValidationErrorType } from 'kea-forms'
import { router } from 'kea-router'

import { urls } from 'scenes/urls'

import { dashboardsModel } from '~/models/dashboardsModel'
import { insightsModel } from '~/models/insightsModel'

import type { DashboardType, QueryBasedInsightModel } from '../../types'

export interface DuplicateDashboardForm {
    dashboardId: number | null
    dashboardName?: string
    duplicateTiles: boolean
    show?: boolean
}

const defaultFormValues: DuplicateDashboardForm = {
    dashboardId: null,
    duplicateTiles: false,
    dashboardName: undefined,
    show: false,
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface duplicateDashboardLogicValues {
    duplicateDashboard: DuplicateDashboardForm
    duplicateDashboardAllErrors: Record<string, any>
    duplicateDashboardChanged: boolean
    duplicateDashboardErrors: DeepPartialMap<DuplicateDashboardForm, ValidationErrorType>
    duplicateDashboardHasErrors: boolean
    duplicateDashboardManualErrors: Record<string, any>
    duplicateDashboardModalVisible: boolean
    duplicateDashboardTouched: boolean
    duplicateDashboardTouches: Record<string, boolean>
    duplicateDashboardValidationErrors: DeepPartialMap<DuplicateDashboardForm, ValidationErrorType>
    isDuplicateDashboardSubmitting: boolean
    isDuplicateDashboardValid: boolean
    showDuplicateDashboardErrors: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface duplicateDashboardLogicActions {
    insightsAddedToDashboard: (args_0: { dashboardId: number; insightIds: number[] }) => {
        dashboardId: number
        insightIds: number[]
    } // insightsModel
    duplicateAndGoToDashboard: () => {
        value: true
    }
    hideDuplicateDashboardModal: () => {
        value: true
    }
    resetDuplicateDashboard: (values?: DuplicateDashboardForm) => {
        values?: DuplicateDashboardForm
    }
    setDuplicateDashboardManualErrors: (errors: Record<string, any>) => {
        errors: Record<string, any>
    }
    setDuplicateDashboardValue: (
        key: FieldName,
        value: any
    ) => {
        name: FieldName
        value: any
    }
    setDuplicateDashboardValues: (values: DeepPartial<DuplicateDashboardForm>) => {
        values: DeepPartial<DuplicateDashboardForm>
    }
    showDuplicateDashboardModal: (
        id: number,
        name: string
    ) => {
        id: number
        name: string
    }
    submitDuplicateDashboard: () => {
        value: boolean
    }
    submitDuplicateDashboardFailure: (
        error: Error,
        errors: Record<string, any>
    ) => {
        error: Error
        errors: Record<string, any>
    }
    submitDuplicateDashboardRequest: (duplicateDashboard: DuplicateDashboardForm) => {
        duplicateDashboard: DuplicateDashboardForm
    }
    submitDuplicateDashboardSuccess: (duplicateDashboard: DuplicateDashboardForm) => {
        duplicateDashboard: DuplicateDashboardForm
    }
    touchDuplicateDashboardField: (key: string) => {
        key: string
    }
}

export type duplicateDashboardLogicType = MakeLogicType<duplicateDashboardLogicValues, duplicateDashboardLogicActions>

export const duplicateDashboardLogic = kea<duplicateDashboardLogicType>([
    path(['scenes', 'dashboard', 'duplicateDashboardLogic']),
    connect(() => ({ actions: [insightsModel, ['insightsAddedToDashboard']] })),
    actions({
        showDuplicateDashboardModal: (id: number, name: string) => ({
            id,
            name,
        }),
        hideDuplicateDashboardModal: true,
        duplicateAndGoToDashboard: true,
    }),
    reducers({
        duplicateDashboardModalVisible: [
            false,
            {
                showDuplicateDashboardModal: () => true,
                hideDuplicateDashboardModal: () => false,
            },
        ],
    }),
    forms(() => ({
        duplicateDashboard: {
            defaults: defaultFormValues,
            errors: () => ({}),
            submit: async ({ dashboardId, dashboardName, show, duplicateTiles }) => {
                if (dashboardId) {
                    dashboardsModel.actions.duplicateDashboard({
                        id: dashboardId,
                        name: dashboardName,
                        show,
                        duplicateTiles,
                    })
                }
            },
        },
    })),
    listeners(({ actions }) => ({
        hideDuplicateDashboardModal: () => {
            actions.resetDuplicateDashboard()
        },
        [dashboardsModel.actionTypes.duplicateDashboardSuccess]: ({
            dashboard,
            payload,
        }: {
            dashboard: DashboardType<QueryBasedInsightModel>
            payload?: Pick<DuplicateDashboardForm, 'duplicateTiles' | 'show'>
        }) => {
            actions.hideDuplicateDashboardModal()

            if (!payload?.duplicateTiles) {
                // any existing mounted insight will need to increment its dashboard count to update turbo mode
                const insightsOnDuplicatedDashboard = dashboard.tiles
                    .map((t) => t.insight?.id)
                    .filter((id): id is number => !!id)
                actions.insightsAddedToDashboard({
                    dashboardId: dashboard.id,
                    insightIds: insightsOnDuplicatedDashboard,
                })
            }

            if (payload?.show) {
                router.actions.push(urls.dashboard(dashboard.id))
            } else {
                if (router.values.currentLocation.pathname !== urls.dashboards()) {
                    router.actions.push(urls.dashboards())
                }
            }
        },
        duplicateAndGoToDashboard: () => {
            actions.setDuplicateDashboardValue('show', true)
            actions.submitDuplicateDashboard()
        },
        showDuplicateDashboardModal: ({ id, name }) => {
            actions.setDuplicateDashboardValues({ dashboardId: id, dashboardName: name })
        },
    })),
])
